import {
  getFaqByIdAPI,
  getFaqCategoriesAPI,
  putFaqByIdAPI,
} from '@/src/apis/rest/faq';
import Button from '@/src/components/atom/Button';
import LucideIcon from '@/src/components/atom/Icons/LucideIcon';
import Input from '@/src/components/atom/Input';
import RadioGroup from '@/src/components/atom/RadioGroup';
import BottomFixedContainer from '@/src/components/molecule/BottomFixedContainer';
import FormContent from '@/src/components/molecule/FormContent';
import MainContentTitle from '@/src/components/molecule/MainContentTitle';
import SectionCard from '@/src/components/molecule/SectionCard';
import { Form, FormField } from '@/src/components/ui/form';
import { BUTTON_CONST } from '@/src/constants/buttons';
import { TOAST_CONST } from '@/src/constants/toast';
import { useToast } from '@/src/hooks/useToast';
import PATH from '@/src/routes/path';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { PutFaqByIdRequestBody } from '@/src/apis/rest/faq.dtos';
import { NAV_CONST } from '@/src/constants/nav';
import Select from '@/src/components/atom/Select';
import TextArea from '@/src/components/atom/TextArea';
import { useEffect, useRef, useState } from 'react';
import { usePrompt } from '@/src/hooks/usePrompt';
import SectionLabel from '@/src/components/molecule/SectionLabel';
import dayjs from 'dayjs';
import { OPTIONS_CONST } from '@/src/constants/options';
import useDialog, { DialogType } from '@/src/hooks/useDialog';
import { COMMON_ALERT_DIALOG_CONST } from '@/src/constants/alertDialog';

const BACK_PATH = PATH.FAQ_DETAIL;

const formErrorMessages = {
  title: '제목을 입력해주세요.',
  contents: '내용을 입력해주세요.',
};

const faqFormSchema = () => {
  return z.object({
    title: z.string().min(1, formErrorMessages.title),
    contents: z.string().min(1, formErrorMessages.contents),
    faqCategoryType: z.string(),
    showedYn: z.boolean(),
  });
};

const DEFAULT_VALUES: Partial<PutFaqByIdRequestBody> = {
  title: '',
  contents: '',
  showedYn: true,
  faqCategoryType: undefined,
};

export default function FaqEditPage() {
  // util
  const navigate = useNavigate();
  const { id } = useParams();
  const { toast } = useToast();

  // query
  const { data: faqCategories } = useQuery({
    queryKey: ['faq-category'],
    queryFn: getFaqCategoriesAPI,
  });

  const { data: faqData } = useQuery({
    queryKey: ['faq', id],
    queryFn: () => getFaqByIdAPI({ id: Number(id) }),
    meta: {
      backUrl: BACK_PATH.replace(':id', String(id)),
      callback: () => {
        navigate(BACK_PATH.replace(':id', String(id)), { replace: true });
      },
    },
  });

  // form
  const form = useForm<PutFaqByIdRequestBody>({
    resolver: zodResolver(faqFormSchema()),
    defaultValues: DEFAULT_VALUES,
    reValidateMode: 'onSubmit',
  });

  // state
  const [defaultData, setDefaultData] = useState('');
  const {
    Dialog: EditDialog,
    isOpen: isEditOpen,
    openDialog: openEditDialog,
    closeDialog: closeEditDialog,
  } = useDialog({
    type: DialogType.ALERT,
  });
  const formRef = useRef<HTMLFormElement>(null);

  // mutation
  const putFaqMutation = useMutation({ mutationFn: putFaqByIdAPI });

  // event
  const handleSubmit = (values: PutFaqByIdRequestBody) => {
    putFaqMutation.mutate(
      { ...values, id: Number(id) },
      {
        onSuccess: () => {
          toast({
            variant: 'success',
            title: TOAST_CONST.saveSuccess.title,
            description: TOAST_CONST.saveSuccess.description,
          });

          navigate(BACK_PATH.replace(':id', String(id)));
        },
        onError: e => {
          toast({
            variant: 'error',
            title: TOAST_CONST.error.title,
            description: e.message,
          });
        },
      },
    );
  };

  // render
  const renderBottomFixedContent = () => {
    return (
      <BottomFixedContainer>
        <div className="flex justify-between">
          <Button
            className="w-max"
            buttonSize="medium"
            buttonColor="outline"
            onClick={() => navigate(BACK_PATH.replace(':id', String(id)))}
          >
            <div className="flex items-center gap-x-2">
              <LucideIcon name="ChevronLeft" size={16} />
              {BUTTON_CONST.backToScreen}
            </div>
          </Button>
          <Button
            disabled={
              !form.formState.isValid ||
              !(defaultData !== JSON.stringify(form.watch()))
            }
            type="button"
            onClick={openEditDialog}
            className="w-40"
            buttonSize="medium"
            isLoading={putFaqMutation.isPending}
          >
            <div className="flex items-center gap-x-2">{BUTTON_CONST.save}</div>
          </Button>
        </div>
      </BottomFixedContainer>
    );
  };

  // effect
  useEffect(() => {
    if (faqData) {
      form.reset(faqData);
      setDefaultData(JSON.stringify(faqData));
    }
  }, [faqData, form]);

  // usePrompt
  usePrompt(
    defaultData !== JSON.stringify(form.watch()) && !form.formState.isSubmitted,
  );

  return (
    <>
      <MainContentTitle
        breadcrumbList={[NAV_CONST.faq, NAV_CONST.faqEdit]}
        pageTitle={NAV_CONST.faqEdit}
      />
      {defaultData ? (
        <Form {...form}>
          <form
            ref={formRef}
            className="relative mb-[100px]"
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            <SectionCard cardTitle="FAQ 정보">
              <div className="grid grid-cols-[1fr_auto_1fr] gap-x-6 gap-y-4">
                <div className="flex flex-col gap-6">
                  <FormField
                    control={form.control}
                    name="faqCategoryType"
                    render={({ field, fieldState: { error } }) => {
                      return (
                        <FormContent isRequired label="카테고리" error={error}>
                          <Select
                            rootProps={{
                              ...field,
                              onValueChange: e => {
                                field.onChange(e);
                              },
                            }}
                            valueProps={{
                              placeholder: '카테고리를 선택해주세요',
                            }}
                            option={[
                              {
                                optionList: (faqCategories || []).map(item => ({
                                  label: item,
                                  value: item,
                                })),
                              },
                            ]}
                            {...field}
                          />
                        </FormContent>
                      );
                    }}
                  />
                  <FormField
                    control={form.control}
                    name="showedYn"
                    render={({ field, fieldState: { error } }) => {
                      return (
                        <FormContent label="노출 여부" error={error}>
                          <RadioGroup
                            optionList={[
                              { label: OPTIONS_CONST.showed, value: 'true' },
                              {
                                label: OPTIONS_CONST.notShowed,
                                value: 'false',
                              },
                            ]}
                            rootProps={{
                              value: field.value ? 'true' : 'false',
                              onValueChange: (e: string) =>
                                field.onChange(e === 'true'),
                            }}
                          />
                        </FormContent>
                      );
                    }}
                  />
                </div>
                <div className="border-r border-r-gray1" />
                <div className="flex flex-col gap-6">
                  <SectionLabel
                    labelProps={{ className: 'translate-y-2' }}
                    label="등록 일시"
                    value={
                      <Input
                        disabled
                        containerClassName="flex-1"
                        value={dayjs(faqData?.createdAt).format(
                          'YYYY-MM-DD HH:mm:ss',
                        )}
                      />
                    }
                  />
                  <SectionLabel
                    labelProps={{ className: 'translate-y-2' }}
                    label="등록 관리자"
                    value={
                      <Input
                        disabled
                        containerClassName="flex-1"
                        value={faqData?.administrator?.name || '-'}
                      />
                    }
                  />
                </div>
                <FormField
                  control={form.control}
                  name="title"
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormContent
                        className="col-span-3"
                        label="제목"
                        isRequired
                        error={error}
                      >
                        <Input
                          containerClassName="flex-1"
                          placeholder="제목을 입력해주세요"
                          onClear={() => field.onChange('')}
                          {...field}
                        />
                      </FormContent>
                    );
                  }}
                />
                <FormField
                  control={form.control}
                  name="contents"
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <FormContent
                        className="col-span-3"
                        label="내용"
                        isRequired
                        error={error}
                      >
                        <TextArea
                          placeholder="FAQ 내용을 입력하세요"
                          className="h-[500px]"
                          {...field}
                        />
                      </FormContent>
                    );
                  }}
                />
              </div>
            </SectionCard>
            {renderBottomFixedContent()}
          </form>
        </Form>
      ) : null}
      {isEditOpen && (
        <EditDialog
          open={isEditOpen}
          onOk={() => formRef?.current?.requestSubmit()}
          onOpenChange={e => {
            if (e) {
              openEditDialog();
            } else {
              closeEditDialog();
            }
          }}
          title={COMMON_ALERT_DIALOG_CONST.edit}
          isLoading={putFaqMutation.isPending}
        />
      )}
    </>
  );
}
